import React from "react";
import { Container, Divider, Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import { ruRU, enUS } from "@mui/x-date-pickers/locales";
import TaskHeader from "../components/TaskHeader";
import TaskForm from "../components/TaskForm";
import TaskView from "../components/TaskView";
import SubtasksAccordion from "../components/Accordions/SubtasksAccordion";
import NotesAccordion from "../components/Accordions/NotesAccordion";
import AttachmentsAccordion from "../components/Accordions/AttachmentsAccordion";
import { useTaskDetail } from "../hooks/useTaskDetail";
import Notification from "../components/Common/Notification";
import DraftPromptDialog from "../components/Common/DraftPromptDialog";

const TaskDetail: React.FC = () => {
  const {
    task,
    isEditing,
    setIsEditing,
    form,
    users,
    mappedSubtasks,
    anchorEl,
    handleTextChange,
    handleSelectChange,
    handleDateChange,
    handleSave,
    handleDelete,
    handleCancel,
    handleOpenMenu,
    handleCloseMenu,
    handleRefresh,
    getStatusConfig,
    id,
    error,
    setError,
    success,
    setSuccess,
    isLoading,
    setNotification,
    draftPromptOpen,
    handleUseDraft,
    handleStartNew,
  } = useTaskDetail();
  const { i18n } = useTranslation();
  const locale = i18n.language === "en_US" ? enUS : ruRU;

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(null);
  };

  if (isLoading || (!task && id !== "new")) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language === "en_US" ? "en" : "ru"}
      localeText={
        locale.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Container sx={{ px: { xs: 0, sm: 2 } }}>
        <TaskHeader
          task={task}
          isEditing={isEditing}
          anchorEl={anchorEl}
          handleOpenMenu={handleOpenMenu}
          handleCloseMenu={handleCloseMenu}
          setIsEditing={setIsEditing}
          handleDelete={handleDelete}
          handleCancel={handleCancel}
          handleSave={handleSave}
          error={error}
          setError={setError}
          success={success}
          setSuccess={setSuccess}
          isLoading={isLoading}
        />
        <Divider sx={{ mb: 2 }} />
        {isEditing || id === "new" ? (
          <TaskForm
            form={form}
            users={users}
            isEditing={isEditing || id === "new"}
            handleTextChange={handleTextChange}
            handleSelectChange={handleSelectChange}
            handleDateChange={handleDateChange}
          />
        ) : task ? (
          <TaskView
            task={task}
            onStatusChange={handleRefresh}
            onStatusUpdateSuccess={(message) => setSuccess(message)}
          />
        ) : null}
        <Box sx={{ mt: 4 }}>
          {id !== "new" && !form.taskParentId && (
            <SubtasksAccordion
              parentId={id!}
              subtasks={mappedSubtasks}
              onRefresh={handleRefresh}
              getStatusConfig={getStatusConfig}
            />
          )}
          {id !== "new" && (
            <NotesAccordion parentId={id!} onRefresh={handleRefresh} />
          )}
          <AttachmentsAccordion
            parentId={id!}
            onRefresh={handleRefresh}
            setNotification={setNotification}
            parentType="Task"
          />
        </Box>
        <Notification
          open={!!error}
          message={error || ""}
          severity="error"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <Notification
          open={!!success}
          message={success || ""}
          severity="success"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <DraftPromptDialog
          open={draftPromptOpen}
          onUseDraft={handleUseDraft}
          onStartNew={handleStartNew}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default TaskDetail;
